import { App } from "@capacitor/app";
import { Camera, CameraResultType, CameraSource, type Photo } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { createId } from "../app/validation";
import { getAppSetting, saveAppSetting } from "../db/appSettingsRepo";
import { createIssueFromPhoto } from "../db/reportRepo";
import { compressImage } from "../media/imageCompression";
import { isNativeApp } from "./runtime";
import { removeNativePhotoCopy } from "./nativePhotoCleanup";

const PENDING_CAPTURE = "native.capture.pending.v1";
export const NATIVE_CAPTURE_CHANGED = "moveinreport:native-capture";
export const NATIVE_CAPTURE_ERROR = "moveinreport:native-capture-error";

export class CameraAccessDeniedError extends Error {
  constructor() {
    super("Camera access is off. Open Settings below to allow it, then return and tap Take photo.");
    this.name = "CameraAccessDeniedError";
  }
}

interface PendingCapture {
  reportId: string;
  roomId: string;
  captureId: string;
  result?: Pick<Photo, "path" | "webPath">;
}

let activeCapture = false;
let recoveryFailed = false;
let saving: Promise<Awaited<ReturnType<typeof createIssueFromPhoto>>> | undefined;

function savePending(pending: PendingCapture) {
  saving ??= (async () => {
    const result = pending.result;
    const source = result?.webPath ?? (result?.path ? Capacitor.convertFileSrc(result.path) : undefined);
    const sourceUrl = source ? new URL(source) : undefined;
    if (!sourceUrl || sourceUrl.protocol !== window.location.protocol || sourceUrl.host !== window.location.host) {
      throw new Error("The selected photo is unavailable. Please choose it again.");
    }
    const response = await fetch(sourceUrl.href);
    if (!response.ok) throw new Error("Could not open the selected photo.");
    const photo = await compressImage(await response.blob());
    const issue = await createIssueFromPhoto(pending.reportId, pending.roomId, photo, pending.captureId);
    // Report media is committed before removing the native camera's working copy.
    // Cache cleanup cannot turn a successful report save into a failed save.
    await removeNativePhotoCopy(result?.path).catch(() => undefined);
    await saveAppSetting(PENDING_CAPTURE, null);
    recoveryFailed = false;
    window.dispatchEvent(new Event(NATIVE_CAPTURE_CHANGED));
    return issue;
  })().finally(() => { saving = undefined; });
  return saving;
}

export function isCaptureCancellation(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? error.code : undefined;
  const message = "message" in error ? error.message : undefined;
  return code === "OS-PLUG-CAMR-0006" || code === "OS-PLUG-CAMR-0020"
    || message === "User cancelled photos app" || message === "User cancelled camera app";
}

export function hasNativeCaptureRecoveryError(): boolean {
  return recoveryFailed;
}

function reportFailure(): void {
  recoveryFailed = true;
  window.dispatchEvent(new Event(NATIVE_CAPTURE_ERROR));
}

export async function retryNativeCapture(): Promise<void> {
  const pending = await getAppSetting<PendingCapture | null>(PENDING_CAPTURE);
  if (pending?.result) await savePending(pending);
}

export async function discardUnsavedNativeCapture(): Promise<void> {
  if (activeCapture || saving) throw new Error("Please wait until the photo finishes saving.");
  const pending = await getAppSetting<PendingCapture | null>(PENDING_CAPTURE);
  await removeNativePhotoCopy(pending?.result?.path).catch(() => undefined);
  // Saved report photos and the user's library are untouched.
  await saveAppSetting(PENDING_CAPTURE, null);
  recoveryFailed = false;
  window.dispatchEvent(new Event(NATIVE_CAPTURE_CHANGED));
}

export async function captureNativePhoto(reportId: string, roomId: string) {
  if (activeCapture) return undefined;
  activeCapture = true;
  try {
    // Finish a previously selected photo before opening another native activity.
    const previous = await getAppSetting<PendingCapture | null>(PENDING_CAPTURE);
    if (previous?.result) await savePending(previous);
    const pending: PendingCapture = { reportId, roomId, captureId: createId("issue") };
    await saveAppSetting(PENDING_CAPTURE, pending);
    let result: Photo;
    try {
      // getPhoto still uses Capacitor's saved activity-call machinery on Android.
      // The newer takePhoto flow in Camera 8.2.4 does not restore its call after process death.
      result = await Camera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        quality: 90,
        width: 2000,
        height: 2000,
        correctOrientation: true,
        saveToGallery: false,
        allowEditing: false
      });
    } catch (error) {
      await saveAppSetting(PENDING_CAPTURE, null);
      if (isCaptureCancellation(error)) return undefined;
      const permissions = await Camera.checkPermissions().catch(() => undefined);
      if (permissions?.camera === "denied") throw new CameraAccessDeniedError();
      throw new Error("The camera could not open. Tap Take photo to try again.");
    }
    pending.result = { path: result.path, webPath: result.webPath };
    await saveAppSetting(PENDING_CAPTURE, pending);
    try {
      return await savePending(pending);
    } catch (error) {
      reportFailure();
      throw error;
    }
  } finally { activeCapture = false; }
}

export async function initializeNativeCaptureRecovery(): Promise<void> {
  if (!isNativeApp()) return;
  await App.addListener("appRestoredResult", (event) => {
    if (event.pluginId !== "Camera" || event.methodName !== "getPhoto") return;
    void (async () => {
      const pending = await getAppSetting<PendingCapture | null>(PENDING_CAPTURE);
      if (!pending) return;
      if (!event.success) {
        await saveAppSetting(PENDING_CAPTURE, null);
        return;
      }
      const result = event.data as Photo;
      if (!result?.path && !result?.webPath) throw new Error("The selected photo is unavailable.");
      pending.result = { path: result.path, webPath: result.webPath };
      await saveAppSetting(PENDING_CAPTURE, pending);
      await retryNativeCapture();
    })().catch(reportFailure);
  });
  await retryNativeCapture().catch(reportFailure);
}
